import { createSlice, PayloadAction } from '@reduxjs/toolkit'

// Constants for localStorage keys
const SIDEBAR_COLLAPSED_KEY = 'pub-sidebar-collapsed'
const SIDEBAR_HIDDEN_KEY = 'pub-sidebar-hidden'

// Helper function to get initial state from localStorage
const getInitialState = () => {
  if (typeof window !== 'undefined') {
    try {
      const storedCollapsed = localStorage.getItem(SIDEBAR_COLLAPSED_KEY)
      const storedHidden = localStorage.getItem(SIDEBAR_HIDDEN_KEY)

      return {
        isCollapsed: storedCollapsed ? JSON.parse(storedCollapsed) : false,
        isHidden: storedHidden ? JSON.parse(storedHidden) : true,
        isMobile: false
      }
    } catch (error) {
      console.error('Error reading sidebar state from localStorage:', error)
    }
  }

  // Default state if localStorage is not available or values can't be parsed
  return {
    isCollapsed: false,
    isHidden: true,
    isMobile: false
  }
}

interface PubSidebarState {
  isCollapsed: boolean
  isHidden: boolean
  isMobile: boolean
}

// Use a function that returns the initialState to ensure it's evaluated when the store is created
const initialState: PubSidebarState = typeof window === 'undefined' ? {
  isCollapsed: false,
  isHidden: true,
  isMobile: false
} : getInitialState()

export const pubSidebarSlice = createSlice({
  name: 'pubSidebar',
  initialState,
  reducers: {
    setCollapsed: (state, action: PayloadAction<boolean>) => {
      state.isCollapsed = action.payload
      if (typeof window !== 'undefined') {
        try {
          localStorage.setItem(SIDEBAR_COLLAPSED_KEY, JSON.stringify(action.payload))
        } catch (error) {
          console.error('Error saving collapsed state to localStorage:', error)
        }
      }
    },
    setHidden: (state, action: PayloadAction<boolean>) => {
      state.isHidden = action.payload
      if (typeof window !== 'undefined') {
        try {
          localStorage.setItem(SIDEBAR_HIDDEN_KEY, JSON.stringify(action.payload))
        } catch (error) {
          console.error('Error saving hidden state to localStorage:', error)
        }
      }
    },
    setMobile: (state, action: PayloadAction<boolean>) => {
      state.isMobile = action.payload
    },
    toggleCollapsed: (state) => {
      state.isCollapsed = !state.isCollapsed
      if (typeof window !== 'undefined') {
        try {
          localStorage.setItem(SIDEBAR_COLLAPSED_KEY, JSON.stringify(state.isCollapsed))
        } catch (error) {
          console.error('Error saving collapsed state to localStorage:', error)
        }
      }
    },
    toggleHidden: (state) => {
      state.isHidden = !state.isHidden
      if (typeof window !== 'undefined') {
        try {
          localStorage.setItem(SIDEBAR_HIDDEN_KEY, JSON.stringify(state.isHidden))
        } catch (error) {
          console.error('Error saving hidden state to localStorage:', error)
        }
      }
    }
  }
})

export const { setCollapsed, setHidden, setMobile, toggleCollapsed, toggleHidden } = pubSidebarSlice.actions
export default pubSidebarSlice.reducer
